import { useState } from "react";
import { IpaType } from "../../phonemes/ipa";
import { RadioList } from "../widgets/RadioList";
import { AccentFormantChart } from "./AccentFormantChart";
import { segmentPhonemes } from "../../phonemes/segmentPhonemes";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { speakPhonemes } from "../../speak/speakPhonemes";
import { CheckBox } from "../widgets/CheckBox";

const long = '\u02d0';
const nasal = '\u0303';
const nonSyllabic = '\u032f';
const none = '(none)';
const middleOptions = [none, 'i̯', 'u̯', 'ə̯'];
const lastOptions = [none, 'i̯', 'u̯', 'ə̯'];
const rhoticOptions = [none, 'ɹ', 'ɾ', 'r', 'ɻ'];
const clickOptions = ['first', 'middle', 'last'];

/** pick monophthong or diphthong from chart & other UI */
export function PhthongPicker(props: {
  readonly toEdit: IpaType,     /** phoneme to edit, may include rhotic that isn't displayed */
  readonly toDisplay: IpaType,  /** phoneme to show in UI */
  readonly reference: IpaType,  /** reference phoneme to show */
  readonly list?: IpaType[],    /** list of additional phonemes to show in chart */
  readonly changed: (phoneme: IpaType) => void, /** control calls when user changes phoneme */
}) {
  const [selectedClick, setSelectedClick] = useState(clickOptions[0]);

  const composer = new PhthongComposer(props.toEdit);
  const middle = composer.getMiddle();
  const last = composer.getLast();
  const rhotic = composer.getRhotic();
  // for lists
  const selectedMiddle = middle === '' ? none : middle;
  const selectedLast = last === '' ? none : last;
  const selectedRhotic = rhotic === '' ? none : rhotic;
  const middleDisabled = last === '';
  const middleList = middleOptions.includes(middle) || middle === '' ? middleOptions : middleOptions.concat(middle);
  const lastList = lastOptions.includes(last) || last === '' ? lastOptions : lastOptions.concat(last);
  const rhoticList = rhoticOptions.includes(rhotic) || rhotic === '' ? rhoticOptions : rhoticOptions.concat(rhotic);

  function changeLength() {
    const phoneme = composer.changeLength();
    props.changed(phoneme);
  }

  function leftClick(ipa: IpaType) {
    switch (selectedClick) {
      case 'first': props.changed(composer.changeFirst(ipa)); break;
      case 'middle': props.changed(composer.changeMiddle(`${ipa}${nonSyllabic}`)); break;
      case 'last': props.changed(composer.changeLast(ipa)); break;
    }
  }

  function changeMiddle(ipa: IpaType) {
    if (ipa === none) ipa = '';
    const phoneme = composer.changeMiddle(ipa);
    props.changed(phoneme);
  }

  function changeEnd(ipa: IpaType) {
    if (ipa === none) ipa = '';
    const phoneme = composer.changeLast(ipa);
    props.changed(phoneme);
  }

  function changeRhotic(ipa: IpaType) {
    if (ipa === none) ipa = '';
    const phoneme = composer.changeRhotic(ipa);
    props.changed(phoneme);
  }

  function changeNasal() {
    const phoneme = composer.changeNasal();
    props.changed(phoneme);
  }

  /*
  function changeLastNasal() {
    const phoneme = composer.changeLastNasal();
    props.changed(phoneme);
  }
  <br /><CheckBox checked={composer.getLastNasal()} onChange={() => changeLastNasal()}>nasal</CheckBox>
  */

  return (<>
    <AccentFormantChart current={props.toDisplay} reference={props.reference} list={props.list} clicked={leftClick} rightClicked={speakPhonemes} />
    <br />
    <>
      click chart to change...
      <RadioList items={clickOptions} selectedItem={selectedClick} horizontal={true} onSelect={setSelectedClick} />
      &nbsp;&nbsp;...sound
    </>
    <br /><br />
    <ColumnAlign>{[
      <span key={0}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>,
      <ColumnAlign key={1}>{[
        <span key={1}>
          first:<br />
          <CheckBox checked={composer.getLong()} onChange={() => changeLength()}>long</CheckBox>
          <br />
          <CheckBox checked={composer.getNasal()} onChange={() => changeNasal()}>nasal</CheckBox>
        </span>,
        <span key={2}>&nbsp;&nbsp;&nbsp;&nbsp;</span>,
        <span key={3}>
          middle:
          <RadioList items={middleList} selectedItem={selectedMiddle} onSelect={changeMiddle} disabled={middleDisabled} />
        </span>,
        <span key={4}>&nbsp;&nbsp;&nbsp;&nbsp;</span>,
        <span key={5}>
          last piece:
          <RadioList items={lastList} selectedItem={selectedLast} onSelect={changeEnd} />
        </span>,
        <span key={6}>&nbsp;&nbsp;&nbsp;&nbsp;</span>,
        <span key={7}>
          rhotic:
          <RadioList items={rhoticList} selectedItem={selectedRhotic} onSelect={changeRhotic} />
        </span>,
        <span key={8}>&nbsp;</span>,
      ]}</ColumnAlign>
    ]}</ColumnAlign>
  </>);
}

/** breaks up & composes mono/di/tri-phthongs w/-or-w/out a final rhotic */
class PhthongComposer {
  private readonly count: number;
  private readonly first: IpaType;
  private readonly middle: IpaType;
  private readonly last: IpaType;
  private readonly rhotic: IpaType;

  constructor(phoneme: IpaType) {
    const segments = segmentPhonemes(phoneme);
    const phonemes = segments.phonemes.map(s => s.phoneme);

    this.count = phonemes.length;
    const lastP = phonemes[phonemes.length - 1];
    this.rhotic = rhoticOptions.includes(lastP) ? lastP : '';
    const rest = rhoticOptions.includes(lastP) ? phonemes.length - 1 : phonemes.length;

    this.first = rest === 0 ? '' : phonemes[0];
    this.middle = rest < 3 ? '' : phonemes[1];
    this.last = rest < 2 ? '' : phonemes[rest - 1];
  }

  getCount() { return this.count; }

  getFirst(): IpaType { return this.first; }
  getMiddle(): IpaType { return this.middle; }
  getLast(): IpaType { return this.last; }
  getRhotic(): IpaType { return this.rhotic; }

  getNasal(): boolean {
    const first = this.getFirst();
    return first.includes(nasal);
  }

  getLong(): boolean {
    const first = this.getFirst();
    return first.includes(long);
  }

  getLastNasal(): boolean {
    const last = this.getLast();
    return last.includes(nasal);
  }

  changeFirst(first: IpaType): IpaType {
    return this.build(first, this.middle, this.last, this.rhotic);
  }

  changeMiddle(middle: IpaType): IpaType {
    return this.build(this.first, middle, this.last, this.rhotic);
  }

  changeLast(last: IpaType): IpaType {
    const newLast = last === 'ɝ' ? 'ɹ'
      : last === 'ə̆' || last === 'ɹ' || last === 'ɾ' || last === 'r' ? last
        : last[last.length - 1] === nonSyllabic ? last
          : last === '' ? ''
            : `${last}${nonSyllabic}`;
    return this.build(this.first, this.middle, newLast, this.rhotic);
  }

  changeRhotic(rhotic: IpaType): IpaType {
    return this.build(this.first, this.middle, this.last, rhotic);
  }

  changeNasal(): IpaType {
    const isLong = this.getLong();
    const isNasal = !this.getNasal();
    const base = this.without(this.first, [long, nasal]);
    const first = `${base}${isNasal ? nasal : ''}${isLong ? long : ''}`;
    return this.build(first, this.middle, this.last, this.rhotic);
  }

  changeLength(): IpaType {
    const wasLong = this.getLong();
    const first = wasLong ? this.without(this.first, [long]) : `${this.first}${long}`;
    return this.build(first, this.middle, this.last, this.rhotic);
  }

  changeLastNasal(): IpaType {
    const isNasal = !this.getLastNasal();
    const base = this.without(this.last, [nasal]);
    const last = `${base}${isNasal ? nasal : ''}`;
    return this.build(this.first, this.middle, last, this.rhotic);
  }

  private without(ipa: IpaType, remove: IpaType[]): IpaType {
    let result = '';
    for (let i = 0; i < ipa.length; i++) {
      if (!remove.includes(ipa[i])) {
        result += ipa[i];
      }
    }
    return result;
  }

  private build(first: IpaType, middle: IpaType, last: IpaType, rhotic: IpaType): IpaType {
    return `${first}${middle}${last}${rhotic}`;
  }
}
